import { createTheme, ThemeProvider as MuiThemeProvider } from "@mui/material";
import { ConfigProvider, theme as antdTheme } from "antd";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export type ThemeMode = "light" | "dark";

// Dark palette mirrors ai/aife (Slate scale) so both web apps look alike.
const DARK_MUI_PALETTE = {
  mode: "dark" as const,
  background: { default: "#0f172a", paper: "#1e293b" },
  text: { primary: "#f1f5f9", secondary: "#94a3b8" },
  divider: "#334155",
};

const DARK_ANTD_TOKEN = {
  colorBgLayout: "#0f172a",
  colorBgContainer: "#1e293b",
  colorBgElevated: "#1e293b",
  colorBorder: "#334155",
  colorBorderSecondary: "#334155",
  colorText: "#f1f5f9",
  colorTextSecondary: "#94a3b8",
  colorTextDescription: "#94a3b8",
};

const STORAGE_KEY = "theme_mode";

interface ThemeModeContextValue {
  mode: ThemeMode;
  isDark: boolean;
  toggleMode: () => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue>({
  mode: "light",
  isDark: false,
  toggleMode: () => undefined,
});

const readStoredMode = (): ThemeMode => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark") return stored;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

export const ThemeModeProvider = ({ children }: { children: ReactNode }) => {
  const [mode, setMode] = useState<ThemeMode>(readStoredMode);
  const isDark = mode === "dark";

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", mode);
    localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  const toggleMode = useCallback(
    () => setMode((previous) => (previous === "dark" ? "light" : "dark")),
    []
  );

  const muiTheme = useMemo(
    () => createTheme({
        palette: isDark ? DARK_MUI_PALETTE : { mode },
        typography: { fontFamily: "inherit" },
      }),
    [mode, isDark]
  );

  const antdConfig = useMemo(
    () => ({
      algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
      token: isDark
        ? DARK_ANTD_TOKEN
        : { colorTextDescription: "#595959" },
    }),
    [isDark]
  );

  const value = useMemo(() => ({ mode, isDark, toggleMode }), [mode, isDark, toggleMode]);

  return (
    <ThemeModeContext.Provider value={value}>
      <MuiThemeProvider theme={muiTheme}>
        <ConfigProvider locale={{ locale: "en" }} theme={antdConfig}>
          {children}
        </ConfigProvider>
      </MuiThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = () => useContext(ThemeModeContext);
